import { patientSeed } from './planState';
import {
  field, grid, chips, card, patientAndTeamCards, goalsCard, medicationListCard,
  monitorCard, emergencyCards, educationCards, signoffCards, basePlan, FREQUENCIES, countDone,
} from './sectionBuilders';
import { normalizePatientChronicConditions } from '../../../../utils/patientChronicConditionUtils';

export function findGeneralCondition(patient) {
  if (!patient) return null;
  const raw = patient.chronic_conditions
    ?? patient.chronicConditions
    ?? patient.icd_codes
    ?? patient.patient_icd_codes
    ?? patient.conditions
    ?? [];
  const list = normalizePatientChronicConditions(raw);
  if (!list.length) return null;
  return list.find((c) => c.billing_role === 'primary') || list[0] || null;
}

const CONDITIONS = [
  'Hypertension', 'Heart failure', 'Diabetes mellitus', 'Chronic kidney disease',
  'COPD', 'Coronary artery disease', 'Obesity', 'Depression', 'Hyperlipidemia',
  'Asthma', 'Chronic pain', 'Other',
];

const PROBLEMS = [
  'Recent hospitalization', 'Medication nonadherence', 'Uncontrolled symptoms',
  'Falls risk', 'Cognitive impairment', 'Limited caregiver support',
  'Food or housing insecurity', 'Health literacy barrier',
];

const MONITORING = [
  { id: 'bp', label: 'Blood pressure', method: 'Home BP cuff' },
  { id: 'wt', label: 'Weight', method: 'Home scale' },
  { id: 'glucose', label: 'Glucose', method: 'Home meter' },
  { id: 'sx', label: 'Symptoms', method: 'Symptom diary' },
  { id: 'meds', label: 'Medication adherence', method: 'Teach-back / refill check' },
  { id: 'lab', label: 'Relevant labs', method: 'Laboratory' },
];

const OUTCOMES = [
  'Symptoms controlled', 'Hospitalizations avoided', 'Medications taken as prescribed',
  'Patient participates in self-management', 'Reversible contributors corrected',
];

const INTERVENTIONS = [
  'Confirm PCP and specialist follow-up', 'Medication reconciliation',
  'Order relevant labs', 'Enroll or continue remote monitoring',
  'Caregiver education', 'Advance care planning discussion', 'Community resource referral',
];

const EDUCATION = [
  'What the condition is and why it matters', 'Warning signs and when to call',
  'Medication purpose, timing and side effects', 'How to use home devices',
  'Managing missed doses safely', 'When to contact the care team',
];

const LIFESTYLE = [
  'Tobacco cessation', 'Limit alcohol', 'Weight management', 'Regular physical activity',
  'Nutrition plan', 'Stress management', 'Adequate sleep',
];

const RESOURCES = [
  'Transportation assistance', 'Home health', 'Pharmacy support / adherence packaging',
  'Financial or prescription-cost assistance', 'Caregiver support / social work', 'Patient support group',
];

function createEmpty(patient) {
  const seed = patientSeed(patient);
  const primaryCond = findGeneralCondition(patient);
  const primaryDiagnosis = primaryCond
    ? (primaryCond.condition_name || primaryCond.icd_code || '')
    : '';
  const diagnosisDate = primaryCond?.onset_date
    ? String(primaryCond.onset_date).slice(0, 10)
    : '';
  return {
    ...basePlan(seed),
    team: { careManager: '', caregiverIds: [], pcp: '', pcpUserId: '', specialist: '', caregiver: '' },
    diagnosis: { primary: primaryDiagnosis, date: diagnosisDate, selected: [], problems: [], notes: '' },
    baseline: { bp: '', hr: '', weight: '', a1c: '', egfr: '', labDate: '', notes: '' },
    risk: { flags: [], functionLevel: '' },
    goals: [
      { id: 'g1', goal: 'Symptoms at goal', target: '', date: '' },
      { id: 'g2', goal: 'Medications taken as prescribed', target: '', date: '' },
    ],
  };
}

function derive(plan) {
  const selected = plan?.diagnosis?.selected || [];
  const flags = [];
  if (!plan?.diagnosis?.primary) flags.push({ level: 'med', text: 'Name the principal condition for this plan.' });
  if (!(plan?.meds?.list || []).length) flags.push({ level: 'low', text: 'Medication list is empty.' });
  if ((plan?.meds?.list || []).length && !plan?.meds?.reconciled) {
    flags.push({ level: 'low', text: 'Medication reconciliation not yet marked complete.' });
  }
  if (!plan?.emergency?.actions) flags.push({ level: 'med', text: 'Emergency action steps are not written yet.' });
  if (!plan?.signoff?.nextReview) flags.push({ level: 'low', text: 'Next review date is not set.' });
  const completion = countDone({
    patient: Boolean(plan?.patient?.name && plan?.patient?.dob && plan?.team?.careManager),
    diagnosis: Boolean(plan?.diagnosis?.primary),
    baseline: Boolean(plan?.baseline?.bp || plan?.baseline?.notes),
    risk: (plan?.risk?.flags || []).length > 0 || Boolean(plan?.risk?.functionLevel),
    goals: (plan?.goals || []).some((goal) => String(goal.date || '').trim()),
    meds: (plan?.meds?.list || []).length > 0,
    monitoring: Object.keys(plan?.monitoring || {}).length > 0,
    emergency: Boolean(plan?.emergency?.actions),
    education: (plan?.education?.topics || []).length > 0,
    signoff: Boolean(plan?.signoff?.managerName && plan?.signoff?.nextReview),
  }, 10);
  return {
    flags,
    completion,
    scoreCards: [],
    tags: selected,
    tagTitle: 'Conditions selected',
  };
}

function sections(program) {
  return [
    { id: 'patient', n: '01', label: 'Patient & care team', cards: patientAndTeamCards(program) },
    {
      id: 'diagnosis', n: '02', label: 'Diagnosis',
      cards: [
        card('Principal condition', [
          grid(2, [
            field('Condition name', 'diagnosis.primary', 'text', { required: true, disabled: true, placeholder: 'e.g. Heart failure, Diabetes' }),
            field('Date of diagnosis', 'diagnosis.date', 'date', { disabled: true }),
          ]),
          {
            type: 'accordion',
            groups: [
              { id: 'related', label: 'Related conditions', path: 'diagnosis.selected', items: CONDITIONS },
              { id: 'problems', label: 'Active problems', path: 'diagnosis.problems', items: PROBLEMS, tone: 'amber' },
            ],
          },
          field('Clinical notes', 'diagnosis.notes', 'textarea', { rows: 3 }),
        ]),
      ],
    },
    {
      id: 'baseline', n: '03', label: 'Baseline data',
      cards: [
        card('Baseline at the time this plan was written', [
          grid(3, [
            field('Blood pressure', 'baseline.bp', 'text', { placeholder: 'e.g. 128/78' }),
            field('Heart rate', 'baseline.hr', 'text', { placeholder: 'e.g. 72' }),
            field('Weight', 'baseline.weight', 'text'),
            field('A1c', 'baseline.a1c', 'text'),
            field('eGFR', 'baseline.egfr', 'text'),
            field('Last lab date', 'baseline.labDate', 'date'),
          ]),
          field('Other baseline notes', 'baseline.notes', 'textarea', { rows: 2 }),
        ]),
      ],
    },
    {
      id: 'risk', n: '04', label: 'Risk',
      cards: [
        card('Risk flags', [
          chips('Risk', 'risk.flags', [
            'Hospitalization in the last 6 months', 'Two or more ED visits this year',
            'High risk of decompensation', 'High risk of falls', 'Polypharmacy (5 or more daily meds)',
          ], { tone: 'red' }),
          field('Functional status', 'risk.functionLevel', 'select', {
            options: ['Independent', 'Needs some help', 'Dependent for daily activities'],
          }),
        ]),
      ],
    },
    { id: 'goals', n: '05', label: 'Goals & outcomes', cards: goalsCard(OUTCOMES, INTERVENTIONS) },
    { id: 'meds', n: '06', label: 'Medications', cards: [medicationListCard()] },
    { id: 'monitoring', n: '07', label: 'Monitoring plan', cards: [monitorCard(MONITORING)] },
    {
      id: 'emergency', n: '08', label: 'Emergency plan',
      cards: emergencyCards('Chest pain, trouble breathing, fainting, one-sided weakness, facial droop, confusion, or uncontrolled bleeding.'),
    },
    { id: 'education', n: '09', label: 'Education & resources', cards: educationCards(EDUCATION, LIFESTYLE, RESOURCES) },
    { id: 'signoff', n: '10', label: 'Review & sign-off', cards: signoffCards(program) },
  ];
}

const generalChronicDisease = {
  id: 'general',
  label: 'Other chronic condition',
  title: 'Chronic condition care plan',
  summary: 'Same care-plan path for a condition that does not have its own disease pack yet.',
  frequencies: FREQUENCIES,
  createEmpty,
  derive,
  sections,
};

export default generalChronicDisease;
